import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useMatches } from "react-router-dom";

import ViewDocumentModal from "../../pages/Documents/components/ViewDocumentModal";
import { useAskPerformYardStore } from "./AskPerformYardStore";
import ChatErrorBoundary from "./components/ChatErrorBoundary";
import ChatPanel from "./components/ChatPanel";
import ChatTrigger from "./components/ChatTrigger";
import ChatWindow from "./components/ChatWindow";

import { useAiSettings, useHasAccessibleDocuments } from "../../api/hooks";
import { useFeatureFlagQuery } from "../../hooks/useFeatureFlag";

import { getUserID } from "../../selectors/app";

import { isInIframe } from "../../utils/isInIframe";

import RouterTags from "../../constants/RouterTags";

/** Check if Ask PerformYard should be visible (used by FeedbackDrawers for positioning). */
export const useAskPerformYardVisible = (): boolean => {
	const { value: aiEnabled } = useFeatureFlagQuery("ff-is-ai-enabled", false);
	const { value: conversationalEnabled } = useFeatureFlagQuery("ff-is-conversational-ui-enabled", false);
	const { data: aiSettings } = useAiSettings();

	const askHrEnabled = aiSettings?.conversationalAiSettings?.askHrEnabled ?? false;
	const aiAssistedGoalCreationEnabled = aiSettings?.conversationalAiSettings?.goalCreationEnabled ?? false;

	const matches = useMatches();
	const tags: string[] =
		matches.length > 0 ? ((matches[matches.length - 1]?.handle as { tags?: string[] })?.tags ?? []) : [];
	const hiddenByRoute = tags.includes(RouterTags.NO_ASK_PERFORMYARD);

	const shouldHide = isInIframe() || hiddenByRoute;

	return aiEnabled && conversationalEnabled && (askHrEnabled || aiAssistedGoalCreationEnabled) && !shouldHide;
};

/** Conversational AI chat interface for answering questions from company documents. */
const AskPerformYard: React.FC = () => {
	const shouldShow = useAskPerformYardVisible();
	const documentModal = useAskPerformYardStore((state) => state.documentModal);
	const closeDocumentModal = useAskPerformYardStore((state) => state.closeDocumentModal);
	const resetChat = useAskPerformYardStore((state) => state.resetChat);
	const setHasNoDocuments = useAskPerformYardStore((state) => state.setHasNoDocuments);

	// Check if the user has any AI-accessible documents
	const { data: accessibleDocs } = useHasAccessibleDocuments();

	useEffect(() => {
		if (accessibleDocs !== undefined) {
			setHasNoDocuments(!accessibleDocs.hasDocuments);
		}
	}, [accessibleDocs, setHasNoDocuments]);

	// Get current user ID to detect super user switches
	const userId = useSelector(getUserID);
	const prevUserIdRef = useRef(userId);

	// Reset chat when user ID changes (e.g., during super usering)
	useEffect(() => {
		if (prevUserIdRef.current !== userId && prevUserIdRef.current !== undefined) {
			resetChat();
		}
		prevUserIdRef.current = userId;
	}, [userId, resetChat]);

	if (!shouldShow) {
		return null;
	}

	return (
		<ChatErrorBoundary>
			<ChatTrigger />
			<ChatWindow />
			<ChatPanel />
			<ViewDocumentModal
				show={documentModal.isOpen}
				onClose={closeDocumentModal}
				document={documentModal.document}
			/>
		</ChatErrorBoundary>
	);
};

export default AskPerformYard;
